import React, { useState } from 'react';
import { Autocomplete, Box, Button, Typography, TextField } from "@mui/material";
import useStyles from "../../../assets/styles/AssessmentPageStyles";
import { useHistory, useLocation } from "react-router-dom";
import { buildInsiderAssessmentService } from "../../../services/assessments.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { LANGUAGES_SUPPORTED, CPCI_ASSESSMENT_NAMES, CPCI_ASSESSMENT_DESCRIPTIONS } from "../../../utils/constants";
import { buildCpciAssessmentPayload } from "../../../utils/cpci.util";

// Fixed presentation for every CPC Index Assessment — no name/icon/colour steps.
// English is the primary copy; per-language entries are applied to translations.
const EN_NAME = CPCI_ASSESSMENT_NAMES['en-US'];
const EN_DESCRIPTION = CPCI_ASSESSMENT_DESCRIPTIONS['en-US'];

// TODO: Multi-language CPC Index Assessment is not enabled yet (English-only for now).
// Flip this to `true` to bring back the language-selection step. The submit logic,
// the <FormLanguageSelector/> component, and the per-language copy in
// CPCI_ASSESSMENT_DESCRIPTIONS are all kept ready — no other change needed.
const ENABLE_LANGUAGE_SELECTION = false;

/**
 * CPC Index Assessment — final step.
 *
 * The CPCI flow is intentionally minimal. For now it is English-only, so this is
 * just a Submit confirmation. Everything is auto-filled — name ("CPCI Assessment"),
 * all indicators (collected on the audience page), default icon/colours, and a
 * fixed English description (CPCI_ASSESSMENT_DESCRIPTIONS).
 *
 * Multi-language support is wired but disabled — see ENABLE_LANGUAGE_SELECTION.
 */
const DQProCpciAssessmentName = () => {
    const classes = useStyles();
    const history = useHistory();
    const { state } = useLocation();
    const buildData = state?.buildData || {};

    const [loading, setLoading] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState([]);

    // English is always generated; users add other languages here.
    const availableLanguages = Object.keys(LANGUAGES_SUPPORTED).filter(lang => lang !== 'en-US');

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Fixed, editable copy per language (no GPT call). Each selected language
            // gets its own localized name + description; both fall back to English.
            const translations = {};
            selectedLanguages.forEach(lang => {
                translations[lang] = {
                    name: CPCI_ASSESSMENT_NAMES[lang] || EN_NAME,
                    description: CPCI_ASSESSMENT_DESCRIPTIONS[lang] || EN_DESCRIPTION,
                };
            });

            const payload = {
                ...buildCpciAssessmentPayload(buildData.user_group, buildData),
                translations,
            };

            await buildInsiderAssessmentService(payload);
            history.push('/dq_pro/assessment/list', { refresh: true });
        } catch (err) {
            console.error('Failed to create CPC Index Assessment:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <LoaderWithBackDrop loading={loading} />
            <Box className={classes.pageContainer} sx={{ pt: 4, mt: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 800, mx: 'auto', px: 2 }}>
                    <Typography variant="h3" className={classes.title} sx={{ mb: 4, textAlign: 'center' }}>
                        {ENABLE_LANGUAGE_SELECTION ? 'Select Language' : 'Create Your Assessment'}
                    </Typography>
                    <Typography sx={{ mb: 5, fontSize: '21px' }}>
                        {ENABLE_LANGUAGE_SELECTION
                            ? <>Your <strong>CPC Index Assessment</strong> is ready. Choose any additional languages you want it available in, or leave blank for English only, then submit.</>
                            : <>Your <strong>CPC Index Assessment</strong> is ready. Click Submit to create it.</>}
                    </Typography>

                    {/* TODO: language selection is disabled for now (English-only).
                        Re-enable by setting ENABLE_LANGUAGE_SELECTION = true above. */}
                    {ENABLE_LANGUAGE_SELECTION && (
                        <FormLanguageSelector
                            availableLanguages={availableLanguages}
                            selectedLanguages={selectedLanguages}
                            setSelectedLanguages={setSelectedLanguages}
                        />
                    )}

                    <Button
                        className={classes.button}
                        variant="contained"
                        sx={{
                            backgroundColor: '#ff6468ff',
                            width: '100%',
                            maxWidth: 360,
                            fontWeight: 'bold',
                            fontSize: '16px',
                            mt: 6,
                            padding: '10px 40px',
                            borderRadius: '20px',
                            '&:hover': { backgroundColor: '#ff6468ff', color: '#ffffff' },
                        }}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Box>
            </Box>
        </>
    );
};

const FormLanguageSelector = ({ availableLanguages, selectedLanguages, setSelectedLanguages }) => {
    const classes = useStyles();
    return (
        <Box sx={{ width: '80%' }}>
            <Autocomplete
                multiple
                className={classes.autoCompField}
                options={availableLanguages}
                getOptionLabel={(lang) => LANGUAGES_SUPPORTED[lang]}
                value={selectedLanguages}
                onChange={(event, newValue) => setSelectedLanguages(newValue)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select Languages (optional)"
                        variant="outlined"
                        InputLabelProps={{
                            style: { color: '#929EA7' },
                            sx: { '&.Mui-focused': { color: '#929EA7' } },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { border: '2px solid #ff6468ff' },
                                '&:hover fieldset': { border: '2px solid #ff6468ff' },
                                '&.Mui-focused fieldset': { border: '2px solid #ff6468ff' },
                            },
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default DQProCpciAssessmentName;
